import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import api from '../api';

const BlogList = ({ classId }) => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedBlog, setSelectedBlog] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('');

    const fetchBlogs = useCallback(async () => {
        try {
            setLoading(true);
            const params = selectedCategory ? `?category=${selectedCategory}` : '';
            const res = await api.get(`/blogs/class/${classId}${params}`);
            // Handle paginated response - blogs endpoint already has pagination
            const blogsData = res.data.blogs?.data || res.data.blogs || [];
            setBlogs(Array.isArray(blogsData) ? blogsData : []);
        } catch (error) {
            console.error('Error fetching blogs:', error);
            setBlogs([]);
        } finally {
            setLoading(false);
        }
    }, [classId, selectedCategory]);

    useEffect(() => {
        if (classId) {
            fetchBlogs();
        }
    }, [classId, selectedCategory, fetchBlogs]);

    const fetchBlogDetail = async (blogId) => {
        try {
            setLoading(true);
            const res = await api.get(`/blogs/${blogId}`);
            setSelectedBlog(res.data);
        } catch (error) {
            console.error('Error fetching blog detail:', error);
            alert('Không thể tải bài viết. Vui lòng thử lại.');
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getCategoryIcon = (category) => {
        const icons = {
            theory: '📚',
            practice: '⚡',
            examples: '💡',
            exercises: '📝',
            news: '📰'
        };
        return icons[category] || '📄';
    };

    const getCategoryName = (category) => {
        const names = {
            theory: 'Lý thuyết',
            practice: 'Thực hành',
            examples: 'Ví dụ',
            exercises: 'Bài tập',
            news: 'Tin tức'
        };
        return names[category] || category;
    };

    const getCategoryColor = (category) => {
        const colors = {
            theory: 'bg-blue-100 text-blue-800',
            practice: 'bg-green-100 text-green-800',
            examples: 'bg-yellow-100 text-yellow-800',
            exercises: 'bg-purple-100 text-purple-800',
            news: 'bg-red-100 text-red-800'
        };
        return colors[category] || 'bg-gray-100 text-gray-800';
    };

    // Math renderer component
    const MathRenderer = ({ children, className = '' }) => {
        if (!children) return null;
        return (
            <div className={`prose prose-slate max-w-none ${className}`}>
                <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                    components={{
                        // Custom styling for different elements
                        h1: ({ children, ...props }) => <h1 className="text-2xl font-bold text-slate-900 mb-4" {...props}>{children}</h1>,
                        h2: ({ children, ...props }) => <h2 className="text-xl font-bold text-slate-800 mb-3 mt-6" {...props}>{children}</h2>,
                        h3: ({ children, ...props }) => <h3 className="text-lg font-semibold text-slate-700 mb-2 mt-4" {...props}>{children}</h3>,
                        p: ({ ...props }) => <p className="text-slate-600 mb-3 leading-relaxed" {...props} />,
                        ul: ({ ...props }) => <ul className="list-disc list-inside text-slate-600 mb-3 space-y-1" {...props} />,
                        ol: ({ ...props }) => <ol className="list-decimal list-inside text-slate-600 mb-3 space-y-1" {...props} />,
                        blockquote: ({ ...props }) => <blockquote className="border-l-4 border-indigo-500 pl-4 py-2 bg-indigo-50 text-slate-700 italic mb-3" {...props} />,
                        code: ({ inline, ...props }) =>
                            inline
                                ? <code className="bg-slate-100 text-slate-800 px-1 py-0.5 rounded text-sm font-mono" {...props} />
                                : <pre className="bg-slate-100 text-slate-800 p-4 rounded-lg overflow-x-auto mb-3"><code {...props} /></pre>
                    }}
                >
                    {children}
                </ReactMarkdown>
            </div>
        );
    };

    if (selectedBlog) {
        return (
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Back Button */}
                <button
                    onClick={() => setSelectedBlog(null)}
                    className="flex items-center text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
                >
                    ← Quay lại danh sách
                </button>

                {/* Article Header */}
                <article className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
                    {/* Cover Image */}
                    {selectedBlog.coverImage && (
                        <div className="mb-6">
                            <img
                                src={`http://localhost:5001${selectedBlog.coverImage}`}
                                alt={selectedBlog.title}
                                className="w-full h-64 object-cover rounded-xl"
                            />
                        </div>
                    )}

                    {/* Title and Meta */}
                    <header className="mb-6">
                        <div className="flex items-center gap-3 mb-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(selectedBlog.category)}`}>
                                {getCategoryIcon(selectedBlog.category)} {getCategoryName(selectedBlog.category)}
                            </span>
                            <span className="text-slate-400">•</span>
                            <span className="text-sm text-slate-500">
                                👁️ {selectedBlog.readCount} lượt đọc
                            </span>
                        </div>

                        <h1 className="text-3xl font-bold text-slate-900 mb-4">
                            {selectedBlog.title}
                        </h1>

                        {selectedBlog.summary && (
                            <p className="text-lg text-slate-600 leading-relaxed mb-4">
                                {selectedBlog.summary}
                            </p>
                        )}

                        <div className="flex items-center text-sm text-slate-500 mb-4">
                            <span>Tác giả: {selectedBlog.author?.name || 'Unknown'}</span>
                            <span className="mx-2">•</span>
                            <span>{formatDateTime(selectedBlog.publishedAt || selectedBlog.createdAt)}</span>
                        </div>

                        {/* Tags */}
                        {selectedBlog.tags && selectedBlog.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {selectedBlog.tags.map((tag) => (
                                    <span key={tag} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700">
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </header>

                    {/* Content */}
                    <div className="prose-lg">
                        <MathRenderer className="text-base">
                            {selectedBlog.content}
                        </MathRenderer>
                    </div>
                </article>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="text-center">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">📚 Lý thuyết & Bài giảng</h2>
                <p className="text-slate-600">Khám phá các bài giảng lý thuyết và tài liệu học tập</p>
            </div>

            {/* Category Filter */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setSelectedCategory('')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            selectedCategory === ''
                                ? 'bg-indigo-600 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                    >
                        📄 Tất cả
                    </button>
                    {['theory', 'practice', 'examples', 'exercises', 'news'].map((category) => (
                        <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                selectedCategory === category
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            {getCategoryIcon(category)} {getCategoryName(category)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Blog List */}
            {loading ? (
                <div className="text-center py-12">
                    <div className="text-lg text-slate-600">Đang tải bài viết...</div>
                </div>
            ) : blogs.length === 0 ? (
                <div className="text-center py-12">
                    <div className="text-6xl mb-4">📚</div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Chưa có bài viết nào</h3>
                    <p className="text-slate-600">
                        {selectedCategory ?
                            `Chưa có bài viết nào trong danh mục "${getCategoryName(selectedCategory)}".` :
                            'Chưa có bài viết lý thuyết nào được xuất bản.'
                        }
                    </p>
                </div>
            ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {blogs.map((blog) => (
                        <article
                            key={blog._id}
                            className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-200 cursor-pointer"
                            onClick={() => fetchBlogDetail(blog._id)}
                        >
                            {/* Cover Image */}
                            {blog.coverImage && (
                                <div className="h-48 overflow-hidden">
                                    <img
                                        src={`http://localhost:5001${blog.coverImage}`}
                                        alt={blog.title}
                                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                    />
                                </div>
                            )}

                            <div className="p-6">
                                {/* Category and Read Count */}
                                <div className="flex items-center justify-between mb-3">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(blog.category)}`}>
                                        {getCategoryIcon(blog.category)} {getCategoryName(blog.category)}
                                    </span>
                                    <span className="text-xs text-slate-500">
                                        👁️ {blog.readCount}
                                    </span>
                                </div>

                                {/* Title */}
                                <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2">
                                    {blog.title}
                                </h3>

                                {/* Summary */}
                                {blog.summary && (
                                    <p className="text-slate-600 text-sm mb-3 line-clamp-3">
                                        {blog.summary}
                                    </p>
                                )}

                                {/* Tags */}
                                {blog.tags && blog.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-3">
                                        {blog.tags.slice(0, 3).map((tag) => (
                                            <span key={tag} className="inline-flex items-center px-2 py-1 rounded text-xs bg-slate-100 text-slate-600">
                                                #{tag}
                                            </span>
                                        ))}
                                        {blog.tags.length > 3 && (
                                            <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-slate-100 text-slate-600">
                                                +{blog.tags.length - 3}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Footer */}
                                <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
                                    <span>{blog.author?.name || 'Unknown'}</span>
                                    <span>{formatDateTime(blog.publishedAt || blog.createdAt)}</span>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BlogList;